import { useContext, useState } from 'react';
import { Box } from '@zillow/constellation-10';
import { ChatBubbleWithLinkFeatureToggle } from '@zg-rentals/satellite-components';
import { AppContext } from '../../contexts/AppContextProvider';
import { useAuth } from '../../AuthProvider';
import MessageActionMenu from '../MessageActionMenu';
import { useUserWarnings } from '../../hooks/useUserWarnings';
import type { TriggerLocation } from '../../constants/clickstreamEvents/globals';
import {
  emitExternalLinkClickedInExternalLinkWarningModalEvent,
  emitOpenExternalLinkWarningModalEvent,
} from '../../constants/clickstreamEvents/externalLinkWarningModal';

type ChatBubbleWithActionsProps = {
  senderName?: string;
  variant: 'autoResponse' | 'messageOwner' | 'default';
  messageContent: string;
  isMessageOwner: boolean;
  listingAlias: string;
  leadId?: string | null;
  triggerLocation: TriggerLocation;
};

export default function ChatBubbleWithActions({
  senderName,
  variant,
  messageContent,
  isMessageOwner,
  listingAlias,
  leadId,
  triggerLocation,
}: ChatBubbleWithActionsProps) {
  const { isMobileDevice } = useContext(AppContext);
  const { user } = useAuth();
  const { externalLinkWarningData } = useUserWarnings();
  const [isMenuTriggerVisible, setIsMenuTriggerVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMouseLeave = () => {
    if (!isMenuOpen) {
      setIsMenuTriggerVisible(false);
    }
  };

  const handleMouseEnter = () => {
    // Mouse enter events interfere with touch start events
    if (!isMobileDevice) {
      setIsMenuTriggerVisible(true);
    }
  };

  const handleTouchStart = () => {
    // Hide all other triggers first to prevent multiple triggers from displaying
    const allTriggers = document.querySelectorAll('.trigger-visible');
    allTriggers.forEach((trigger) => {
      trigger.classList.remove('trigger-visible');
    });

    // Reset component state to false and use setTimeout to ensure
    // DOM update happens before setting new state.
    setIsMenuTriggerVisible(false);
    setTimeout(() => {
      setIsMenuTriggerVisible(true);
    }, 0);
  };

  return (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexDirection: isMessageOwner ? 'row-reverse' : 'row',
        gap: 'tight',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
    >
      <ChatBubbleWithLinkFeatureToggle
        senderName={senderName}
        variant={variant}
        enableClickableLinks
        externalLinkWarningData={externalLinkWarningData}
        onExternalLinkWarningModalOpen={() =>
          emitOpenExternalLinkWarningModalEvent({
            user,
            listing: { listingAlias },
            triggerLocation,
            lead: { leadId },
          })
        }
        onExternalLinkClickInModal={() =>
          emitExternalLinkClickedInExternalLinkWarningModalEvent({
            user,
            listing: { listingAlias },
            triggerLocation,
            lead: { leadId },
          })
        }
        enableHistoryIntegration={true}
      >
        {messageContent}
      </ChatBubbleWithLinkFeatureToggle>
      <MessageActionMenu
        isTriggerVisible={isMenuTriggerVisible}
        messageContent={messageContent}
        setIsMenuOpen={setIsMenuOpen}
        setIsTriggerVisible={setIsMenuTriggerVisible}
      />
    </Box>
  );
}
